import { ApolloLink } from "@apollo/client"
import { Observable, defer, map, retry, throwError, timer } from "rxjs"
import { print } from "graphql"
import { generateClient } from "aws-amplify/api"
import type { GraphQLSubscription } from "aws-amplify/api"

import { isConnectivityError } from "helpers/isConnectivityError"
import { describeNetworkError, isTransientSubscriptionError } from "helpers/networkErrorReporting"

/**
 * Bridges GraphQL subscription operations onto Amplify's AppSync realtime
 * transport. Amplify speaks AppSync's proprietary WebSocket protocol (handshake
 * auth + start/data/ka framing) natively and uses browser-native encoding, so
 * no Node Buffer polyfill is needed — unlike the generic graphql-ws client,
 * which AppSync rejects at the WS upgrade.
 *
 * Both Amplify v6 and Apollo Client 4 emit RxJS Observables, so the bridge
 * reshapes Amplify's { data } messages into Apollo's result shape.
 * Queries/mutations stay on the HTTP link (see ApolloClientProvider).
 *
 * Amplify does NOT retry a failed subscription: it errors the observer and at
 * most reconnects the socket, so without help a token expiry or reconnect race
 * silently kills the subscription for the rest of the tab's life. The retry
 * below resubscribes on those transient failures — each attempt gets a fresh
 * subscription id and re-fetches the auth header, which cures all of them.
 * Errors that survive the retries (or are not transient) propagate to the
 * error link and are recorded to RUM.
 */
const client = generateClient()

const MAX_RETRIES = 8
const BASE_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 30000
// An attempt that stayed subscribed this long clearly connected, so the next
// failure counts as a new incident with a fresh retry budget. Within one
// incident, failures always arrive well inside this window (≤30s backoff plus
// connection/ack time). Without the reset, routine hourly token expiries
// would drain the budget over a workday and kill the subscription.
const INCIDENT_RESET_MS = 60000

const isRecoverable = (error: unknown): boolean =>
  isConnectivityError(error) ||
  isTransientSubscriptionError(describeNetworkError(error, "").message)

const exhaustedRetriesError = (error: unknown): Error => {
  const { message } = describeNetworkError(error, "unknown cause")
  const exhausted = new Error(`Subscription retries exhausted (${MAX_RETRIES} attempts): ${message}`)
  exhausted.name = "SubscriptionRetriesExhausted"
  return exhausted
}

export const amplifySubscriptionLink = new ApolloLink((operation) => {
  // Retry accounting is per incident, not per subscription lifetime: rxjs's
  // own retryCount never resets (and resetOnSuccess only resets on a *value*,
  // which a quiet subscription may not see for hours), so we track failures
  // ourselves and reset when the failed attempt had lived long enough to have
  // been a healthy connection.
  let attemptStartedAt = 0
  let failuresThisIncident = 0

  return defer(() => {
    attemptStartedAt = Date.now()
    return client.graphql<GraphQLSubscription<Record<string, unknown>>>({
      query: print(operation.query),
      variables: operation.variables
    }) as Observable<{ data: Record<string, unknown> }>
  }).pipe(
    map((message): ApolloLink.Result => ({ data: message.data })),
    retry({
      delay: (error: unknown) => {
        if (!isRecoverable(error)) {
          return throwError(() => error)
        }
        if (Date.now() - attemptStartedAt > INCIDENT_RESET_MS) {
          failuresThisIncident = 0
        }
        failuresThisIncident += 1
        if (failuresThisIncident > MAX_RETRIES) {
          return throwError(() => exhaustedRetriesError(error))
        }
        const delayMs = Math.min(
          BASE_RETRY_DELAY_MS * 2 ** (failuresThisIncident - 1),
          MAX_RETRY_DELAY_MS
        )
        // eslint-disable-next-line no-console
        console.log(
          `[Subscription error - transient, retry ${failuresThisIncident}/${MAX_RETRIES} in ${delayMs}ms]: ${operation.operationName}`
        )
        return timer(delayMs)
      }
    })
  )
})
